<template>
  <el-dialog
    title="选择车牌号"
    :visible.sync="visible"
    width="520px"
    class="plate-dialog"
    append-to-body
    @close="$emit('update:visible', false)"
  >
    <!-- 车牌预览 -->
    <div class="plate-preview-box">
      <div class="plate-display" :class="plateColorClass">
        <span class="plate-inner">
          <span class="plate-cell" :class="{ 'cell-pro': province, empty: !province }">{{ province || '省' }}</span>
          <span class="plate-cell" :class="{ 'cell-let': letter, empty: !letter }">{{ letter || 'A' }}</span>
          <span
            v-for="(_, i) in maxLen"
            :key="i"
            class="plate-cell"
            :class="{ 'cell挂': digits[i] === '挂', empty: !digits[i] }"
          >{{ digits[i] || '*' }}</span>
        </span>
      </div>
      <div class="plate-toggle">
        <el-radio-group v-model="isNewEnergy" size="mini" @change="onNewEnergyChange">
          <el-radio-button :label="false">普通</el-radio-button>
          <el-radio-button :label="true">新能源</el-radio-button>
        </el-radio-group>
      </div>
      <div class="plate-meta">
        <span v-if="colorInfo" class="plate-color-dot" :class="`dot-${colorInfo.code}`"></span>
        <span v-if="colorInfo" class="plate-color-name">{{ colorInfo.name }}</span>
        <span class="plate-count">{{ fullPlate.length }}/{{ isNewEnergy ? 8 : 7 }}位</span>
        <span class="plate-hint" v-if="!fullPlate">请依次选择</span>
      </div>
    </div>

    <!-- 操作区 -->
    <div class="plate-body">
      <!-- 省份 -->
      <div class="plate-row">
        <div class="plate-row-label">
          <span class="step-num" :class="{ done: !!province }">{{ province ? '✓' : '1' }}</span>
          <span class="step-text">省份</span>
          <span class="step-cur" v-if="province">{{ province }}</span>
        </div>
        <div class="plate-row-btns">
          <el-button
            v-for="p in provinces" :key="p"
            size="mini"
            :class="{ active: province === p }"
            @click="province = p"
          >{{ p }}</el-button>
        </div>
      </div>

      <!-- 字母 -->
      <div class="plate-row">
        <div class="plate-row-label">
          <span class="step-num" :class="{ done: !!letter }">{{ letter ? '✓' : '2' }}</span>
          <span class="step-text">字母</span>
          <span class="step-cur" v-if="letter">{{ letter }}</span>
        </div>
        <div class="plate-row-btns">
          <el-button
            v-for="l in letters" :key="l"
            size="mini"
            :disabled="!province"
            :class="{ active: letter === l }"
            @click="letter = l"
          >{{ l }}</el-button>
        </div>
      </div>

      <!-- 后段 + 新能源 -->
      <div class="plate-row">
        <div class="plate-row-label">
          <span class="step-num" :class="{ done: isValid }">{{ isValid ? '✓' : '3' }}</span>
          <span class="step-text">后段</span>
          <span class="step-cur" v-if="digits">{{ digits }}</span>
          <span class="step-tip">{{ digits.length }}/{{ maxLen }}</span>
        </div>
        <div class="plate-row-btns">
          <el-button
            v-for="k in keypad" :key="k"
            size="mini"
            :disabled="isKeyDisabled(k)"
            :class="{
              'active': lastDigit === k,
              'sel': digits.includes(k) && lastDigit !== k,
              'del': k === 'DEL',
              'gua': k === '挂'
            }"
            @click="k === 'DEL' ? deleteChar() : inputChar(k)"
          >
            <i v-if="k === 'DEL'" class="el-icon-back" style="font-size:14px"></i>
            <span v-else>{{ k }}</span>
          </el-button>
        </div>
      </div>
    </div>

    <div slot="footer" class="plate-footer">
      <div class="plate-footer-tip">
        <span v-if="!isValid && !province">请选择省份</span>
        <span v-else-if="!isValid && !letter">请选择字母</span>
        <span v-else-if="!isValid">还需输入 {{ maxLen - digits.length }} 位</span>
      </div>
      <div class="plate-footer-btns">
        <el-button size="small" @click="$emit('update:visible', false)">取消</el-button>
        <el-button size="small" type="primary" :disabled="!isValid" @click="handleConfirm">确认</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
const PROVINCES = ['京','津','沪','渝','冀','豫','云','辽','黑','湘','皖','鲁','新','苏','浙','赣','鄂','桂','甘','晋','蒙','陕','吉','闽','贵','粤','川','青','琼','宁','藏']
const LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ'.split('')
const KEYPAD = ['0','1','2','3','4','5','6','7','8','9','A','B','C','D','E','F','G','H','J','K','L','M','N','P','Q','R','S','T','U','V','W','X','Y','Z','挂','DEL']

export default {
  name: 'PlateNumberSelector',
  props: {
    visible: Boolean,
    value: { type: String, default: '' }
  },
  data() {
    return {
      province: '',
      letter: '',
      digits: '',
      isNewEnergy: false,
      lastDigit: '',
      provinces: PROVINCES,
      letters: LETTERS,
      keypad: KEYPAD
    }
  },
  watch: {
    visible(val) {
      if (val) this.parseValue(this.value)
    }
  },
  computed: {
    maxLen() { return this.isNewEnergy ? 6 : 5 },
    fullPlate() {
      return this.province + this.letter + this.digits
    },
    colorInfo() {
      const p = this.fullPlate
      // 未选完时，根据当前类型预览颜色
      if (!p || p.length < 2) return null
      const requiredLen = this.isNewEnergy ? 8 : 7
      if (p.length !== requiredLen) return null
      if (this.isNewEnergy) return { name: '新能源绿牌', code: 'green' }
      return { name: '货车黄牌', code: 'yellow' }
    },
    plateColorClass() {
      return this.colorInfo ? `plate-${this.colorInfo.code}` : ''
    },
    isValid() {
      return !!this.colorInfo
    }
  },
  methods: {
    isLetter(k) {
      return LETTERS.includes(k)
    },
    parseValue(val) {
      if (!val || val.length < 2) {
        this.province = ''
        this.letter = ''
        this.digits = ''
        this.isNewEnergy = false
        this.lastDigit = ''
        return
      }
      this.province = val[0]
      // 验证第二位是否是有效字母
      if (this.isLetter(val[1])) {
        this.letter = val[1]
        this.digits = val.slice(2)
      } else {
        // 如果不是字母，清空letter，让用户重新选择
        this.letter = ''
        this.digits = val.slice(1)
      }
      this.isNewEnergy = val.length === 8
      this.lastDigit = this.digits ? this.digits[this.digits.length - 1] : ''
    },
    onNewEnergyChange(val) {
      // 过滤掉"挂"字符
      this.digits = this.digits.replace(/挂/g, '')
      if (val) {
        if (this.digits.length < 6) {
          this.digits = this.digits.padEnd(6, '')
        } else if (this.digits.length > 6) {
          this.digits = this.digits.slice(0, 6)
        }
      } else {
        if (this.digits.length > 5) {
          this.digits = this.digits.slice(0, 5)
        }
      }
      this.lastDigit = this.digits ? this.digits[this.digits.length - 1] : ''
    },
    isKeyDisabled(k) {
      if (k === 'DEL') return false
      // 必须先选择省份和字母才能输入后段
      if (!this.province || !this.letter) return true
      if (this.digits.length >= this.maxLen) return true
      if (k === '挂' && (this.isNewEnergy || this.digits.length !== this.maxLen - 1)) return true
      return false
    },
    inputChar(k) {
      if (this.isKeyDisabled(k)) return
      this.digits += k
      this.lastDigit = k
    },
    deleteChar() {
      this.digits = this.digits.slice(0, -1)
      this.lastDigit = this.digits ? this.digits[this.digits.length - 1] : ''
    },
    handleConfirm() {
      this.$emit('confirm', {
        truckCode: this.fullPlate,
        colorCode: this.colorInfo.code,
        colorName: this.colorInfo.name
      })
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.plate-dialog >>> .el-dialog {
  border-radius: 6px;
}
.plate-dialog >>> .el-dialog__header {
  border-bottom: 1px solid #eee;
  padding: 14px 20px;
}
.plate-dialog >>> .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}
.plate-dialog >>> .el-dialog__body {
  padding: 0;
}
.plate-dialog >>> .el-dialog__footer {
  padding: 10px 16px;
  border-top: 1px solid #eee;
}

/* === 车牌预览 === */
.plate-preview-box {
  background: #f5f7fa;
  border-bottom: 1px solid #eee;
  padding: 14px 16px 12px;
  text-align: center;
}
.plate-display {
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  border: 2px solid #dcdfe6;
  background: #fff;
  overflow: hidden;
  margin-bottom: 6px;
  transition: all 0.2s;
}
.plate-display.plate-blue   { background: #1a6fc4; border-color: #1559b0; }
.plate-display.plate-green { background: #2e8b57; border-color: #1e6b43; }
.plate-display.plate-yellow{ background: #f7c948; border-color: #d4a810; }

.plate-inner {
  display: flex;
  align-items: center;
}
.plate-cell {
  width: 36px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  font-family: Arial, sans-serif;
  border-right: 1px solid rgba(0,0,0,0.08);
  background: #fff;
  color: #333;
  flex-shrink: 0;
}
.plate-cell.empty {
  color: #d0d4db;
  background: #fafafa;
}
.plate-cell.cell-pro,
.plate-cell.cell-let {
  background: rgba(0,0,0,0.05);
  color: #265e47;
  font-size: 22px;
}
.plate-display.plate-blue   .plate-cell.cell-pro,
.plate-display.plate-blue   .plate-cell.cell-let,
.plate-display.plate-green  .plate-cell.cell-pro,
.plate-display.plate-green  .plate-cell.cell-let {
  background: rgba(255,255,255,0.18);
  color: #fff;
}
.plate-display.plate-blue   .plate-cell.cell-pro,
.plate-display.plate-blue   .plate-cell.cell-let { background: rgba(255,255,255,0.25); }
.plate-display.plate-green  .plate-cell.cell-pro,
.plate-display.plate-green  .plate-cell.cell-let { background: rgba(255,255,255,0.15); }

/* 黄牌：黑字 */
.plate-display.plate-yellow .plate-cell { color: #1a1a1a; }
.plate-display.plate-yellow .plate-cell.empty { color: #b8a000; background: #fdf5cc; }
.plate-display.plate-yellow .plate-cell.cell-pro,
.plate-display.plate-yellow .plate-cell.cell-let {
  background: rgba(0,0,0,0.08);
  color: #1a1a1a;
}

.plate-cell.cell挂 { color: #7a5c00; background: #fff8d6; }
.plate-display.plate-yellow .plate-cell.cell挂 { color: #5a4000; background: rgba(0,0,0,0.12); }

.plate-toggle {
  display: flex;
  justify-content: center;
  margin: 6px 0 4px;
}
.plate-toggle >>> .el-radio-group {
  display: flex;
}
.plate-toggle >>> .el-radio-button__inner {
  padding: 4px 14px;
  font-size: 13px;
  border-radius: 0;
  height: 28px;
  line-height: 20px;
  background: #f4f4f5;
  border-color: #e4e4e7;
  color: #909399;
  box-shadow: none;
}
.plate-toggle >>> .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #265e47;
  border-color: #265e47;
  color: #fff;
  box-shadow: none;
}
.plate-toggle >>> .el-radio-button:first-child .el-radio-button__inner {
  border-radius: 3px 0 0 3px;
}
.plate-toggle >>> .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 0 3px 3px 0;
}

.plate-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
}
.plate-color-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-blue   { background: #1a6fc4; }
.dot-green  { background: #2e8b57; }
.dot-yellow { background: #f7c948; }
.plate-color-name { font-weight: 600; color: #333; }
.plate-count { color: #909399; }
.plate-hint { color: #c0c4cc; }

/* === 操作区 === */
.plate-body { padding: 6px 0; }

.plate-row {
  padding: 6px 16px;
  border-bottom: 1px solid #f0f0f0;
}
.plate-row:last-child { border-bottom: none; }

.plate-row-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 13px;
}
.step-num {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid #dcdfe6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #909399;
  flex-shrink: 0;
}
.step-num.done {
  background: #265e47;
  border-color: #265e47;
  color: #fff;
}
.step-text { color: #606060; font-size: 13px; }
.step-cur {
  font-weight: 700;
  font-size: 16px;
  color: #265e47;
}
.step-tip {
  font-size: 12px;
  color: #c0c4cc;
  margin-left: auto;
  font-family: Arial, sans-serif;
}
.plate-row-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

/* 按钮 — 与 Element UI 原生风格一致 */
.plate-row-btns >>> .el-button {
  height: 30px;
  padding: 0 9px;
  font-size: 14px;
  font-weight: 600;
  font-family: Arial, sans-serif;
  border-radius: 4px;
  color: #606060;
  background: #f4f4f5;
  border-color: #e4e4e7;
  transition: all 0.12s;
}
.plate-row-btns >>> .el-button:hover {
  background: #ecf5ef;
  border-color: #265e47;
  color: #265e47;
}
.plate-row-btns >>> .el-button.active {
  background: #265e47;
  border-color: #265e47;
  color: #fff;
  box-shadow: 0 0 0 1px #265e47 inset;
}
.plate-row-btns >>> .el-button.sel {
  background: #e8f3ec;
  border-color: #a3d0b5;
  color: #265e47;
}
.plate-row-btns >>> .el-button.del {
  background: #fef0f0;
  border-color: #f56c6c;
  color: #f56c6c;
}
.plate-row-btns >>> .el-button.gua {
  background: #fdf6ec;
  border-color: #e6a23c;
  color: #e6a23c;
}
.plate-row-btns >>> .el-button.is-disabled {
  opacity: 0.3;
}

/* === 底部 === */
.plate-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.plate-footer-tip { font-size: 13px; color: #c0c4cc; }
.plate-footer-btns >>> .el-button {
  border-radius: 4px;
  padding: 8px 18px;
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// src/components/PlateNumberSelector.vue